import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc,
  updateDoc,
  getDoc,
  query, 
  orderBy, 
  limit,
  where,
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { db } from '../firebase/config';

interface Word {
  id: string;
  english_word: string;
  turkish_word: string;
  example_sentence: string;
  example_sentence_turkish: string;
  difficulty: number;
}

interface UserProgress {
  id: string;
  userId: string;
  wordId: string;
  learnedCount: number;
  lastReviewed: any;
  nextReview: any;
  isLearned: boolean;
}

// Fisher-Yates shuffle algorithm
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const useWordManager = (userId: string | null) => {
  const [words, setWords] = useState<Word[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [poolSize, setPoolSize] = useState(200);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const [shuffledWords, setShuffledWords] = useState<Word[]>([]);

  // Progress map for fast lookup
  const progressByWordId: Record<string, UserProgress> = userProgress.reduce((acc, p) => {
    acc[p.wordId] = p;
    return acc;
  }, {} as Record<string, UserProgress>);

  // Compute per-difficulty stats and current difficulty
  const computeDifficultyStats = () => {
    const byLevelTotals: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const byLevelLearned: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const byLevelStarted: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    // Precompute word lookup by id -> difficulty
    const wordDifficultyById: Record<string, number> = {};
    for (const w of words) {
      byLevelTotals[w.difficulty] = (byLevelTotals[w.difficulty] || 0) + 1;
      wordDifficultyById[w.id] = w.difficulty;
    }

    for (const p of userProgress) {
      const d = wordDifficultyById[p.wordId];
      if (!d) continue;
      byLevelStarted[d] = (byLevelStarted[d] || 0) + 1;
      if (p.isLearned) {
        byLevelLearned[d] = (byLevelLearned[d] || 0) + 1;
      }
    }

    // Determine current difficulty: first level where learned < 80% of total
    let current = 5;
    for (let diff = 1; diff <= 5; diff++) {
      const total = byLevelTotals[diff] || 0;
      const learned = byLevelLearned[diff] || 0;
      if (total === 0) { current = diff; break; }
      if (learned < total * 0.8) { current = diff; break; }
    }

    const breakdown = [1,2,3,4,5].map(level => {
      const total = byLevelTotals[level] || 0;
      const learned = byLevelLearned[level] || 0;
      const started = byLevelStarted[level] || 0;
      const inProgress = Math.max(0, started - learned);
      const notStarted = Math.max(0, total - started);
      const percent = total > 0 ? (learned / total) * 100 : 0;
      return { level, total, learned, inProgress, notStarted, percent };
    });

    return { currentDifficulty: current, breakdown, byLevelLearned, byLevelTotals };
  };

  const { currentDifficulty, breakdown: difficultyBreakdown, byLevelLearned, byLevelTotals } = computeDifficultyStats();

  // Build pool with weighted mixing across difficulties
  const poolWords: Word[] = (() => {
    const notLearned = (w: Word) => !(progressByWordId[w.id]?.isLearned);
    const byLevel: Record<number, Word[]> = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    for (const w of shuffledWords) {
      if (notLearned(w)) byLevel[w.difficulty].push(w);
    }

    // Quotas: for level n>1, allow floor(learnedAt(n-1) * 0.2)
    const selected: Word[] = [];
    const usedIds = new Set<string>();

    const pushSome = (arr: Word[], count: number) => {
      for (const w of arr) {
        if (selected.length >= poolSize) break;
        if (count <= 0) break;
        if (usedIds.has(w.id)) continue;
        selected.push(w);
        usedIds.add(w.id);
        count--;
      }
    };

    // Reserve quotas from higher levels first so they are guaranteed to appear
    for (let lvl = currentDifficulty + 1; lvl <= 5; lvl++) {
      const learnedPrev = byLevelLearned[(lvl - 1) as 1|2|3|4|5] || 0;
      const quota = Math.max(0, Math.floor(learnedPrev * 0.2));
      if (quota > 0) {
        pushSome(byLevel[lvl], quota);
      }
    }

    // Fill the rest from current difficulty
    if (selected.length < poolSize) {
      pushSome(byLevel[currentDifficulty], poolSize - selected.length);
    }

    // If still room, backfill from subsequent higher levels without quota limits
    if (selected.length < poolSize) {
      for (let lvl = currentDifficulty + 1; lvl <= 5; lvl++) {
        if (selected.length >= poolSize) break;
        pushSome(byLevel[lvl], poolSize - selected.length);
      }
    }

    // If still not enough (edge case), backfill from lower levels
    if (selected.length < poolSize) {
      for (let lvl = currentDifficulty - 1; lvl >= 1; lvl--) {
        if (selected.length >= poolSize) break;
        pushSome(byLevel[lvl], poolSize - selected.length);
      }
    }

    return selected;
  })();

  // Compute ordered queue: due first, then by learnedCount and difficulty
  const now = new Date();
  const isDue = (p?: UserProgress) => {
    if (!p) return true; // unseen words are due to be introduced
    if (!p.nextReview) return true;
    const nr = p.nextReview?.toDate ? p.nextReview.toDate() : new Date(p.nextReview);
    return nr <= now;
  };

  // Memoize visibleQueue to prevent unnecessary re-renders
  const visibleQueue: Word[] = React.useMemo(() => {
    const filteredWords = poolWords.filter((w) => !(progressByWordId[w.id]?.isLearned));
    
    // Use a completely stable sort based on word ID only
    return filteredWords.sort((a, b) => a.id.localeCompare(b.id));
  }, [poolWords, progressByWordId]);

  // Expand pool when enough words in current pool are learned
  const maybeExpandPool = () => {
    const learnedInPool = poolWords.filter((w) => progressByWordId[w.id]?.isLearned).length;
    const learnedRatio = poolWords.length > 0 ? learnedInPool / poolWords.length : 0;
    // If at least 40% of pool learned, add 50 more words
    if (learnedRatio >= 0.4 && poolSize < words.length) {
      setPoolSize((s) => Math.min(s + 50, words.length));
    }
  };

  // Date helpers for streaks
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const isSameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime();
  const isYesterday = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(new Date(b.getTime() - 24 * 60 * 60 * 1000)).getTime();

  // Firebase'den kelimeleri yükle
  const loadWords = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      
      // Kelimeleri yükle
      const wordsRef = collection(db, 'words');
      const q = query(wordsRef, orderBy('difficulty', 'asc'), limit(5000));
      const querySnapshot = await getDocs(q);
      
      const loadedWords: Word[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        loadedWords.push({
          id: doc.id,
          english_word: data.english_word,
          turkish_word: data.turkish_word,
          example_sentence: data.example_sentence,
          example_sentence_turkish: data.example_sentence_turkish,
          difficulty: data.difficulty,
        });
      });

      setWords(loadedWords);
      
      // Shuffle words for randomization
      setShuffledWords(shuffleArray(loadedWords));
      
      // Kullanıcı ilerlemesini yükle
      await loadUserProgress(userId);
    } catch (error) {
      console.error('Error loading words:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcı ilerlemesini yükle
  const loadUserProgress = async (uid: string) => {
    try {
      // Önce Firebase'den yüklemeyi dene
      const progressRef = collection(db, 'user_progress');
      const q = query(progressRef, where('userId', '==', uid));
      const querySnapshot = await getDocs(q);
      
      const loadedProgress: UserProgress[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        loadedProgress.push({
          id: doc.id,
          userId: data.userId,
          wordId: data.wordId,
          learnedCount: data.learnedCount || 0,
          lastReviewed: data.lastReviewed,
          nextReview: data.nextReview,
          isLearned: data.isLearned || false,
        });
      });

      setUserProgress(loadedProgress);
    } catch (error) {
      console.log('Firebase quota exceeded, loading from localStorage');
      
      // Firebase hatası durumunda localStorage'dan yükle
      try {
        const offlineProgress = JSON.parse(localStorage.getItem(`offline_progress_${uid}`) || '[]');
        const loadedProgress: UserProgress[] = offlineProgress.map((item: any) => ({
          id: item.id,
          userId: item.userId,
          wordId: item.wordId,
          learnedCount: item.learnedCount || 0,
          lastReviewed: new Date(item.lastReviewed),
          nextReview: new Date(item.nextReview),
          isLearned: item.isLearned || false,
        }));
        
        setUserProgress(loadedProgress);
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
        setUserProgress([]);
      }
    }
  };

  // Kelime görüldü (başlandı) - notStarted sayısını azaltmak için kayıt aç
  const markAsSeen = async (wordId: string) => {
    if (!userId) return;
    
    // Mevcut ilerlemeyi kontrol et
    const existingProgress = userProgress.find(p => p.wordId === wordId);
    if (existingProgress) {
      console.log('Word already seen:', wordId);
      return; // already has a record
    }

    console.log('Marking word as seen:', wordId);
    const now = new Date();
    const progressId = `${userId}_${wordId}`;

    // Local state'i güncelle
    setUserProgress(prev => ([...prev, {
      id: progressId,
      userId,
      wordId,
      learnedCount: 0,
      lastReviewed: now,
      nextReview: now,
      isLearned: false,
    }]));

    // LocalStorage'a kaydet (offline mode)
    try {
      const offlineProgress = JSON.parse(localStorage.getItem(`offline_progress_${userId}`) || '[]');
      offlineProgress.push({
        id: progressId,
        userId,
        wordId,
        learnedCount: 0,
        lastReviewed: now.toISOString(),
        nextReview: now.toISOString(),
        isLearned: false,
      });
      localStorage.setItem(`offline_progress_${userId}`, JSON.stringify(offlineProgress));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Firebase'e kaydet (eğer quota yoksa)
    try {
      const progressRef = doc(db, 'user_progress', progressId);
      await setDoc(progressRef, {
        userId,
        wordId,
        learnedCount: 0,
        lastReviewed: now,
        nextReview: now,
        isLearned: false,
      }, { merge: true });
    } catch (error) {
      console.log('Firebase quota exceeded, using offline mode');
      // Firebase hatası durumunda offline mode'da devam et
    }
  };

  // Kelime öğrenildi (sağa swipe) - 3 kez doğru = öğrenildi
  const markAsLearned = async (wordId: string) => {
    if (!userId) return;
    
    const now = new Date();
    const progressId = `${userId}_${wordId}`;
    
    // Mevcut ilerlemeyi bul veya yeni oluştur
    let currentProgress = userProgress.find(p => p.wordId === wordId);
    const newLearnedCount = (currentProgress?.learnedCount || 0) + 1;
    
    // Öğrenme sayısına göre tekrar süresini hesapla
    let nextReviewDays = 1;
    if (newLearnedCount === 1) nextReviewDays = 1;
    else if (newLearnedCount === 2) nextReviewDays = 3;
    else if (newLearnedCount >= 3) nextReviewDays = 7; // 3 kez doğru = öğrenildi

    const nextReview = new Date(now.getTime() + nextReviewDays * 24 * 60 * 60 * 1000);
    const isLearned = newLearnedCount >= 3; // 3 kez doğru = öğrenildi

    console.log('Marking word as learned:', wordId, 'count:', newLearnedCount, 'isLearned:', isLearned);

    // Local state'i güncelle
    setUserProgress(prev => {
      const filtered = prev.filter(p => p.wordId !== wordId);
      return [...filtered, {
        id: progressId,
        userId,
        wordId,
        learnedCount: newLearnedCount,
        lastReviewed: now,
        nextReview: nextReview,
        isLearned,
      }];
    });

    // LocalStorage'a kaydet (offline mode)
    try {
      const offlineProgress = JSON.parse(localStorage.getItem(`offline_progress_${userId}`) || '[]');
      const existingIndex = offlineProgress.findIndex((p: any) => p.wordId === wordId);
      
      if (existingIndex >= 0) {
        offlineProgress[existingIndex] = {
          ...offlineProgress[existingIndex],
          learnedCount: newLearnedCount,
          lastReviewed: now.toISOString(),
          nextReview: nextReview.toISOString(),
          isLearned,
        };
      } else {
        offlineProgress.push({
          id: progressId,
          userId,
          wordId,
          learnedCount: newLearnedCount,
          lastReviewed: now.toISOString(),
          nextReview: nextReview.toISOString(),
          isLearned,
        });
      }
      localStorage.setItem(`offline_progress_${userId}`, JSON.stringify(offlineProgress));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Firebase'e kaydet (eğer quota yoksa)
    try {
      const progressRef = doc(db, 'user_progress', progressId);
      await setDoc(progressRef, {
        userId,
        wordId,
        learnedCount: newLearnedCount,
        lastReviewed: serverTimestamp(),
        nextReview: nextReview,
        isLearned,
      }, { merge: true });
    } catch (error) {
      console.log('Firebase quota exceeded, using offline mode');
      // Firebase hatası durumunda offline mode'da devam et
    }
  };

  // Kelime öğrenilmedi (sola swipe)
  const markAsNotLearned = async (wordId: string) => {
    if (!userId) return;
    
    const now = new Date();
    const progressId = `${userId}_${wordId}`;
    
    // Mevcut ilerlemeyi bul veya yeni oluştur
    let currentProgress = userProgress.find(p => p.wordId === wordId);
    const newLearnedCount = Math.max(0, (currentProgress?.learnedCount || 0) - 1);
    
    // Öğrenilmediyse daha sık tekrarla (1 gün sonra)
    const nextReview = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const isLearned = newLearnedCount >= 3; // 3 kez doğru = öğrenildi

    // Local state'i güncelle
    setUserProgress(prev => {
      const filtered = prev.filter(p => p.wordId !== wordId);
      return [...filtered, {
        id: progressId,
        userId,
        wordId,
        learnedCount: newLearnedCount,
        lastReviewed: now,
        nextReview: nextReview,
        isLearned,
      }];
    });

    // LocalStorage'a kaydet (offline mode)
    try {
      const offlineProgress = JSON.parse(localStorage.getItem(`offline_progress_${userId}`) || '[]');
      const existingIndex = offlineProgress.findIndex((p: any) => p.wordId === wordId);
      
      if (existingIndex >= 0) {
        offlineProgress[existingIndex] = {
          ...offlineProgress[existingIndex],
          learnedCount: newLearnedCount,
          lastReviewed: now.toISOString(),
          nextReview: nextReview.toISOString(),
          isLearned,
        };
      } else {
        offlineProgress.push({
          id: progressId,
          userId,
          wordId,
          learnedCount: newLearnedCount,
          lastReviewed: now.toISOString(),
          nextReview: nextReview.toISOString(),
          isLearned,
        });
      }
      localStorage.setItem(`offline_progress_${userId}`, JSON.stringify(offlineProgress));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Firebase'e kaydet (eğer quota yoksa)
    try {
      const progressRef = doc(db, 'user_progress', progressId);
      await setDoc(progressRef, {
        userId,
        wordId,
        learnedCount: newLearnedCount,
        lastReviewed: serverTimestamp(),
        nextReview: nextReview,
        isLearned,
      }, { merge: true });
    } catch (error) {
      console.log('Firebase quota exceeded, using offline mode');
      // Firebase hatası durumunda offline mode'da devam et
    }
  };

  // Sonraki kelimeye geç
  const nextWord = () => {
    maybeExpandPool();
    if (visibleQueue.length === 0) return;
    
    // Use a more stable approach
    const currentWord = getCurrentWord();
    if (!currentWord) return;
    
    const currentIndex = visibleQueue.findIndex(w => w.id === currentWord.id);
    if (currentIndex === -1) return;
    
    const nextIndex = (currentIndex + 1) % visibleQueue.length;
    const nextWord = visibleQueue[nextIndex];
    
    if (nextWord && userId) {
      localStorage.setItem(`lastWord_${userId}`, nextWord.id);
    }
    
    setCurrentQueueIndex(nextIndex);
  };

  // Şu anki kelimeyi al
  const getCurrentWord = () => {
    if (visibleQueue.length === 0) return null;
    // Clamp index without causing additional state changes here
    const safeIndex = Math.max(0, Math.min(currentQueueIndex, visibleQueue.length - 1));
    return visibleQueue[safeIndex] || null;
  };

  // Reset queue index if queue shrinks or on words/progress changes
  useEffect(() => {
    if (visibleQueue.length === 0) return;
    if (currentQueueIndex >= visibleQueue.length) {
      // If current index is out of bounds due to list shrinking, pin to last item
      setCurrentQueueIndex(visibleQueue.length - 1);
    }
  }, [visibleQueue.length, currentQueueIndex]);

  // Prevent rapid state updates
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  // Öğrenme istatistikleri: "başlanmış" = progress kaydı olan tüm kelimeler
  const getStats = () => {
    const totalWords = words.length;
    const learnedWords = userProgress.filter(p => p.isLearned).length;
    const startedWords = userProgress.length; // herhangi bir kayıt = başlanmış
    const inProgressWords = Math.max(0, startedWords - learnedWords);
    const notStartedWords = Math.max(0, totalWords - startedWords);

    // Debug mesajını sadece development'ta göster
    if (process.env.NODE_ENV === 'development') {
      console.log('Stats Debug:', {
        totalWords,
        userProgressLength: userProgress.length,
        learnedWords,
        startedWords,
        inProgressWords,
        notStartedWords,
        sampleProgress: userProgress.slice(0, 3)
      });
    }

    return {
      total: totalWords,
      learned: learnedWords,
      inProgress: inProgressWords,
      notStarted: notStartedWords,
      currentDifficulty,
      difficultyBreakdown: difficultyBreakdown,
    };
  };

  // Kullanıcı giriş yaptığında kelimeleri yükle
  useEffect(() => {
    if (userId) {
      loadWords();
    }
  }, [userId]);

  // Restore last position after words/progress load
  useEffect(() => {
    if (!userId || visibleQueue.length === 0) return;
    const lastId = localStorage.getItem(`lastWord_${userId}`);
    if (!lastId) return;
    const pos = visibleQueue.findIndex(w => w.id === lastId);
    if (pos >= 0) setCurrentQueueIndex(pos);
  }, [userId, visibleQueue.length]);

  return {
    words,
    currentWord: getCurrentWord(),
    loading,
    userProgress,
    markAsLearned,
    markAsNotLearned,
    markAsSeen,
    loadWords,
    getStats,
    currentDifficulty,
    nextWord,
  };
};
